import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { ITableOrder } from './CrudDataTable'
import { IHeader } from './IHeader'
import { isUndefined, sortBy } from 'lodash-es'
import { sortByKey } from 'utilities/sortByKey'
import { ITableProps } from './ITableProps'
import { isSelectable } from './data-table.hook'
import DataTableEvent, {
    DataTableEventParams,
    DataTableEventType,
} from 'components/DataTable/events/dataTableEvent'
import useDataTableEventSubscription from 'hooks/useDataTableEventSubscription/useDataTableEventSubscription'

interface IDataTableContext<T> {
    onAllCollapse: (open: boolean) => void
    openCollapses: string[]
    onToggleCollapse: (itemKey: string) => void
    forceOpenCollapse: boolean
    setOpenForceCollapse: (forceOpen: boolean) => void
    hasCollapseData: boolean
    sortColOrders: ITableOrder[]
    updateSortHandler: (sortKey?: string) => void
    page: number
    rowPerPage: number
    handleOnChangePage: (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => void
    handleOnChangeRowPerPage: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void
    paginationData: T[]
    selectableRowCount: number
}

const customComparator = (
    order: 'asc' | 'desc' | undefined,
    comparator: (a: any, b: any, c?: any) => number,
    key?: string,
) => {
    return order === 'desc'
        ? (a: any, b: any) => -comparator(a, b, key)
        : (a: any, b: any) => comparator(a, b, key)
}

const multiColComparator = (
    sortColOrders: ITableOrder[],
    headers: IHeader[],
    aItem: any,
    bItem: any,
) => {
    let compare = (a: any, b: any) => {
        if (typeof a === 'string' && typeof b === 'string')
            return a.localeCompare(b)
    }
    for (let colOrder of sortBy(sortColOrders, (o) => o.isUniqueColumn)) {
        var curCol = headers.find(
            (a) =>
                a.label?.toString().toLowerCase() ===
                colOrder.column.toLowerCase(),
        )
        if (curCol) {
            if (curCol.comparator)
                compare = customComparator(
                    colOrder.direction,
                    curCol.comparator,
                )
            else if (typeof curCol.value === 'string') {
                compare = customComparator(
                    colOrder.direction,
                    sortByKey,
                    curCol.value,
                )
            }
        }
        //skip col sort if column name not found
        else continue
        let result = compare(aItem, bItem)
        if (result !== 0) return result
    }
}

const stableSort = (
    sortColOrders: ITableOrder[],
    headers: IHeader[],
    array: any[],
) => {
    const stabilizedThis = array.map((el: any, index: Number) => [el, index])

    stabilizedThis.sort((a: any, b: any) => {
        const order = multiColComparator(sortColOrders, headers, a[0], b[0])
        if (order && order !== 0) return order
        return a[1] - b[1]
    })
    return [...stabilizedThis.map((el: any) => el[0])]
}

export function DataTableContextProvider<T>({
    children,
    props,
}: {
    children: ReactNode
    props: ITableProps<T>
}) {
    const {
        defaultOpenCollapse = false,
        headers,
        data,
        usePagination = true,
        initOrder = [],
        initialRowPerPage = -1,
        hasCollapse,
        rowOption,
        onChangePage,
        ignoreSortedRow,
        allowMultiSorting = true,
        onSyncSortedData,
        disableAutoSetFirstPage,
        id,
        disabledAutoSort,
    } = props
    const [openCollapses, setOpenCollapses] = useState<string[]>([])
    const [forceOpenCollapse, setOpenForceCollapse] = useState<boolean>(
        defaultOpenCollapse,
    )
    const [rowPerPage, setRowPerPage] = useState<number>(initialRowPerPage)
    const [page, setPage] = useState<number>(0)
    const [sortColOrders, setSortColOrders] = useState<ITableOrder[]>(
        initOrder ?? [],
    )
    const rowOptionResolved = useMemo(() => rowOption || {}, [rowOption])
    const hasCollapseData = useMemo(
        () =>
            hasCollapse ||
            (isUndefined(hasCollapse) &&
                data.some((a) => ((a as any).childrenItems?.length ?? 0) > 0)),
        [data, hasCollapse],
    )

    const handleOnChangePage = useCallback(
        (
            event: React.MouseEvent<HTMLButtonElement> | null,
            newPage: number,
        ) => {
            setPage(newPage)
            onChangePage && onChangePage(newPage)
        },
        [onChangePage],
    )

    const handleOnChangeRowPerPage = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setRowPerPage(parseInt(event.target.value, 10))
            setPage(0)
        },
        [],
    )

    const sortedData = useMemo(() => {
        if (disabledAutoSort) return data
        const excludedPinRows = data.filter(
            (c) => !(ignoreSortedRow?.(c) ?? false),
        )
        const topRows = data
            .filter((c) => ignoreSortedRow?.(c) ?? false)
            .concat(stableSort(sortColOrders, headers, excludedPinRows))

        return topRows
    }, [disabledAutoSort, data, sortColOrders, headers, ignoreSortedRow])

    const paginationData = useMemo(() => {
        if (sortedData)
            return rowPerPage > 0 && usePagination
                ? sortedData.slice(
                      page * rowPerPage,
                      page * rowPerPage + rowPerPage,
                  )
                : sortedData
        else return []
    }, [sortedData, page, rowPerPage, usePagination])

    const selectableRowCount = useMemo(() => {
        return data.filter((e) => {
            return isSelectable(e, rowOptionResolved)
        }).length
    }, [data, rowOptionResolved])

    const updateSortHandler = useCallback(
        (sortKey?: string) => {
            if (!sortKey) return

            let selectedCol = headers.find(
                (h) => h.label?.toString().toLowerCase() === sortKey,
            )

            if (selectedCol) {
                setSortColOrders((oldOrders) => {
                    let index = oldOrders.findIndex(
                        (o) => o.column.toLowerCase() === sortKey,
                    )
                    if (index === -1) {
                        return [
                            ...(oldOrders.filter(
                                (o) =>
                                    // If allowMultiSorting = FALSE -> remove all old orders
                                    allowMultiSorting &&
                                    // There should be only 1 col of Unique Col
                                    (!Boolean(selectedCol?.isUniqueColumn) ||
                                        !o.isUniqueColumn),
                            ) ?? []),
                            {
                                column: sortKey,
                                direction: 'asc',
                                isUniqueColumn: Boolean(
                                    selectedCol?.isUniqueColumn,
                                ),
                            },
                        ]
                    } else {
                        if (oldOrders[index].direction === 'asc') {
                            oldOrders[index].direction = 'desc'
                        } else if (oldOrders[index].direction === 'desc') {
                            oldOrders.splice(index, 1)
                        }
                        return [...oldOrders]
                    }
                })
            }
        },
        [headers, allowMultiSorting],
    )

    const onAllCollapse = useCallback((open: boolean) => {
        if (open) setOpenForceCollapse(true)
        else setOpenCollapses([])
    }, [])

    const onToggleCollapse = useCallback((itemKey: string) => {
        setOpenCollapses((prevs) => {
            if (prevs.includes(itemKey))
                return prevs.filter((i) => i && !i.includes(itemKey))
            else return [...prevs, itemKey]
        })
    }, [])

    useEffect(() => {
        if (!disableAutoSetFirstPage) setPage(0)
    }, [data, disableAutoSetFirstPage])

    useEffect(() => {
        if (sortedData) onSyncSortedData && onSyncSortedData(sortedData)
    }, [onSyncSortedData, sortedData])

    const onRequestedData = useCallback(
        (param: DataTableEventParams) => {
            if (sortedData) {
                if (
                    param.type === DataTableEventType.OnPrintRequest &&
                    param.targetTableID &&
                    param.targetTableID === id
                ) {
                    console.log(`trigger next`)
                    DataTableEvent.next({
                        type: DataTableEventType.OnReceivedPrintData,
                        data: sortedData,
                        targetTableID: id,
                        onReceivedAction: param.onReceivedAction,
                        carryOverParams: param.carryOverParams,
                    })
                }
            }
        },
        [id, sortedData],
    )
    useDataTableEventSubscription(onRequestedData)

    return (
        <DataTableContext.Provider
            value={{
                onAllCollapse,
                openCollapses,
                onToggleCollapse,
                forceOpenCollapse,
                setOpenForceCollapse,
                hasCollapseData,
                sortColOrders,
                updateSortHandler,
                page,
                rowPerPage,
                handleOnChangePage,
                handleOnChangeRowPerPage,
                paginationData,
                selectableRowCount,
                ...props,
            }}
        >
            {children}
        </DataTableContext.Provider>
    )
}

const DataTableContext = createContext<
    IDataTableContext<any> & ITableProps<any>
>({
    openCollapses: [],
    onToggleCollapse: function (itemKey: string): void {
        throw new Error('Function not implemented.')
    },
    forceOpenCollapse: false,
    setOpenForceCollapse: function (forceOpen: boolean): void {
        throw new Error('Function not implemented.')
    },
    onAllCollapse: function (open: boolean): void {
        throw new Error('Function not implemented.')
    },
    tableMaxHeight: undefined,
    stickyHeader: undefined,
    hiddenHeader: false,
    data: [],
    headers: [],
    itemKey: '',
    hasCollapseData: false,
    noExpandableItems: false,
    sortColOrders: [],
    updateSortHandler: function (sortKey?: string): void {
        throw new Error('Function not implemented.')
    },
    page: 0,
    rowPerPage: 0,
    handleOnChangePage: function (event: any, newPage: number): void {
        throw new Error('Function not implemented.')
    },
    handleOnChangeRowPerPage: function (event: any): void {
        throw new Error('Function not implemented.')
    },
    paginationData: [],
    selectableRowCount: 0,
})

export const useDataTableContext = () => {
    return useContext(DataTableContext)
}

export default DataTableContext
